import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * PromoHero — full-width MP4 hero.
 *
 * Behaviour:
 *   - Autoplays muted on every device (only way autoplay is allowed by
 *     modern browsers without a user gesture).
 *   - Loops seamlessly.
 *   - `playsinline` keeps iOS Safari from taking over the screen.
 *   - Custom overlay button toggles sound; single tap = full audio.
 *   - Responsive 16:9 that fills any viewport width.
 */
export default function PromoHero({ src, poster, title = "ADN Local promo trailer" }) {
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);
    const [showHint, setShowHint] = useState(true);
    const [hasError, setHasError] = useState(false);

    // Kick off playback explicitly for browsers that ignore the `autoplay`
    // attribute until React finishes hydrating (Safari + some Chromium builds).
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const tryPlay = () => v.play().catch(() => { /* user-gesture required */ });
        tryPlay();
        const t = setTimeout(tryPlay, 400);
        return () => clearTimeout(t);
    }, []);

    // Auto-hide the "Tap for sound" chip after 8s so it doesn't linger.
    useEffect(() => {
        if (!muted) return setShowHint(false);
        const t = setTimeout(() => setShowHint(false), 8000);
        return () => clearTimeout(t);
    }, [muted]);

    const toggleSound = () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        setMuted(v.muted);
        if (!v.muted) {
            // Ensure playback continues after unmute (browsers sometimes pause).
            v.play().catch(() => { /* ignore */ });
        }
    };

    return (
        <section className="relative w-full bg-[#0A1128]" data-testid="promo-hero">
            <div className="relative w-full aspect-video">
                <video
                    ref={videoRef}
                    poster={poster}
                    title={title}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onError={() => setHasError(true)}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="promo-hero-video"
                >
                    <source src={src} type='video/mp4; codecs="avc1.640028, mp4a.40.2"' />
                    <source src={src} type="video/mp4" />
                </video>
                {hasError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[#0A1128] text-white/80 text-sm text-center px-6" data-testid="promo-hero-fallback">
                        Your browser can&rsquo;t play this trailer inline. <a href={src} className="underline text-[#D4AF37] ml-2" target="_blank" rel="noopener noreferrer">Tap to watch</a>
                    </div>
                )}
                {/* Non-interactive gradient for text legibility if we ever
                    stack a caption on top of the video. */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0A1128]/70 to-transparent" />

                {/* Tap-to-unmute button — bottom-right, thumb-friendly */}
                <button
                    type="button"
                    onClick={toggleSound}
                    aria-pressed={!muted}
                    aria-label={muted ? "Unmute video" : "Mute video"}
                    className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-[#0A1128]/85 hover:bg-[#0A1128] text-[#D4AF37] border border-[#D4AF37]/60 backdrop-blur flex items-center justify-center shadow-lg transition-all hover:scale-110"
                    data-testid="promo-hero-mute-toggle"
                >
                    {muted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                </button>
                {muted && showHint && (
                    <div className="pointer-events-none absolute bottom-4 right-20 sm:bottom-8 sm:right-24 hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0A1128]/85 border border-[#D4AF37]/40 text-[#D4AF37] text-[11px] uppercase tracking-widest">
                        Tap for sound
                    </div>
                )}
            </div>
        </section>
    );
}
